import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCurrentUser } from '../util/amplifyAuth';
import { acceptTerms, checkFocuses } from '../components/API';
import Spinner from '../components/Spinner/Spinner';
import '../styles/Signup.css';
import '../styles/AcceptTerms.css';
import '../styles/LegalPage.css';

function AcceptTerms() {
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                await getCurrentUser();
            } catch {
                navigate('/login', { replace: true });
            }
        })();
    }, [navigate]);

    const handleAccept = async () => {
        if (!agreed) return;
        setSubmitting(true);
        setError('');
        try {
            await acceptTerms();
            const skipUntil = localStorage.getItem('bloodeye_focus_skipped_until');
            if (skipUntil && Date.now() < parseInt(skipUntil, 10)) {
                navigate('/home');
            } else {
                const { hasFocuses } = await checkFocuses().catch(() => ({ hasFocuses: false }));
                navigate(hasFocuses ? '/home' : '/select-focus');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="signup-page gate-scrollable">
            <div className="signup-overlay">
                <div className="signup-modal accept-terms-modal">
                    <div className="signup-flow-step">
                        <h1>Privacy Policy &amp; Terms</h1>
                        <p className="signup-subtitle">
                            Please review and accept our Privacy Policy and Terms of Service to continue.
                        </p>

                        {error && <div className="signup-error">{error}</div>}

                        <div className="legal-scroll-container">
                            <h2>Privacy Policy Summary</h2>
                            <p>
                                <strong>PrimeMaxing is not a healthcare provider.</strong> Our platform is an
                                informational tool that helps you understand your lab results through automated
                                analysis and AI-generated insights. All scores, suggestions, and content
                                are for <strong>informational purposes only</strong> and are not medical advice.
                            </p>
                            <p>
                                We collect your account information, uploaded lab data, and usage data to provide
                                the Service. Your health data is encrypted at rest and in transit. We do not sell
                                your personal health information. You can request deletion of your data at any time.
                            </p>
                            <p>
                                While we may offer access to licensed healthcare providers through the platform,
                                those providers are independent third parties responsible for their own medical advice.
                            </p>
                            <p>
                                <Link to="/legal/data-protection" target="_blank" rel="noopener noreferrer">
                                    Read the full Privacy Policy
                                </Link>
                            </p>

                            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />

                            <h2>Terms of Service Summary</h2>
                            <p>
                                The Service is provided "as is" without warranties. You acknowledge that PrimeMaxing
                                is <strong>not a substitute for professional medical advice</strong>, diagnosis, or
                                treatment. Always consult a qualified healthcare professional before making health
                                decisions.
                            </p>
                            <p>
                                You are responsible for maintaining your account security and ensuring that uploaded
                                data belongs to you.
                            </p>
                            <p>
                                <Link to="/legal/service-agreement" target="_blank" rel="noopener noreferrer">
                                    Read the full Terms of Service
                                </Link>
                            </p>
                        </div>

                        <label className="accept-terms-checkbox">
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                            />
                            <span>
                                I have read and agree to the{' '}
                                <Link to="/legal/data-protection" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                                {' '}and{' '}
                                <Link to="/legal/service-agreement" target="_blank" rel="noopener noreferrer">Terms of Service</Link>.
                                I understand that PrimeMaxing is not a medical provider and that all content
                                is for informational purposes only.
                            </span>
                        </label>

                        <button
                            className="signup-submit"
                            onClick={handleAccept}
                            disabled={!agreed || submitting}
                        >
                            {submitting ? <Spinner size={20} color="#fff" /> : 'Accept & Continue'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AcceptTerms;
